import { defineStore } from "pinia";
import { normalizzaSceltaSfondo, SCELTA_SFONDO_PREDEFINITA, type SceltaSfondo } from "~/utils/mappaBasemap";

// Chiave del tema: `$localStorage` ci aggiunge il suffisso del progetto (isDarkTheme_saet).
// Fino al 23/10/2026 la chiave era scritta senza suffisso: la scelta già fatta si sposta in silenzio
// sulla chiave nuova (migrazione in caricaTemaSalvato), così nessuno deve rifare la scelta del tema.
// La stessa chiave è letta da app/spa-loading-template.html (lo guarda un test).
const CHIAVE_TEMA = "isDarkTheme";
const CHIAVE_TEMA_VECCHIA = "isDarkTheme";
// Sfondo delle mappe (30/10/2026): una scelta sola per dashboard, dettaglio impianto e vista dall'alto. Le chiavi di prima (una per mappa) non si migrano: i valori
// delle mappe tolte non esistono più, si riparte da «automatica».
const CHIAVE_SFONDO_MAPPA = "sfondoMappa";
// Ultimo impianto guardato nelle pagine di storico (2/11/2026): al ritorno si riparte da lì, senza riscegliere ogni volta
const CHIAVE_ULTIMO_IMPIANTO_STORICO = "ultimoImpiantoStorico";

function applicaClasseTema(scuro: boolean): void {
  if (import.meta.client) document.documentElement.classList.toggle("dark", scuro);
}

// Cambio di tema dall'utente: tutti i colori devono passare insieme. Senza questo, gli elementi che hanno una `transition` sul
// colore (voci del menu, bottoni, marchio) arrivano al nuovo colore con 150-200 ms di ritardo, mentre fondi e vetro cambiano di
// colpo e il testo sembra «in ritardo». Per un attimo la classe `cambio-tema` spegne tutte le transizioni (base.css).
function applicaTemaSenzaTransizioni(scuro: boolean): void {
  if (!import.meta.client) return;
  const radice = document.documentElement;
  radice.classList.add("cambio-tema");
  applicaClasseTema(scuro);
  // Niente lettura di layout forzata (`offsetHeight` dava «Forced reflow» in console, 40 ms con molte superfici di vetro): la classe
  // `cambio-tema` e quella del tema arrivano insieme, quindi il primo ricalcolo degli stili avviene già con le transizioni spente;
  // `cambio-tema` si toglie due frame dopo, quando quel ricalcolo è sicuramente avvenuto.
  requestAnimationFrame(() => requestAnimationFrame(() => radice.classList.remove("cambio-tema")));
}

// Stato dell'interfaccia condiviso tra pagine e componenti: il tema chiaro/scuro, il menu laterale e lo sfondo delle mappe.
export const useUiStore = defineStore("ui", () => {
  // Preso qui, nel contesto di setup: dentro le azioni useNuxtApp() non è garantito.
  const { $localStorage } = useNuxtApp();

  const isDarkTheme = ref(false);
  // true se l'utente ha già scelto (o rifiutato) il tema: serve a non rifare il suggerimento
  const temaScelto = ref(false);
  // menu laterale (sidebar): si apre dal pulsante in testa a ogni pagina o con Ctrl+B
  const menuAperto = ref(false);
  // sfondo delle mappe: `auto` (segue il tema), `chiara`, `scura` o `satellite`
  const sfondoMappa = ref<SceltaSfondo>(leggiSfondoMappaSalvato());

  // ultimo impianto scelto nelle pagine di storico (null = nessuno)
  const ultimoImpiantoStorico = ref<number | null>(leggiUltimoImpiantoStorico());

  function leggiUltimoImpiantoStorico(): number | null {
    if (!import.meta.client) return null;
    try {
      const id = Number.parseInt($localStorage.getItem(CHIAVE_ULTIMO_IMPIANTO_STORICO) ?? "", 10);
      return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  function impostaUltimoImpiantoStorico(id: number | null): void {
    ultimoImpiantoStorico.value = id;
    try {
      if (id === null) $localStorage.removeItem(CHIAVE_ULTIMO_IMPIANTO_STORICO);
      else $localStorage.setItem(CHIAVE_ULTIMO_IMPIANTO_STORICO, String(id));
    } catch {
      // archivio non disponibile: vale solo finché la pagina è aperta
    }
  }

  function leggiSfondoMappaSalvato(): SceltaSfondo {
    if (!import.meta.client) return SCELTA_SFONDO_PREDEFINITA;
    try {
      return normalizzaSceltaSfondo($localStorage.getItem(CHIAVE_SFONDO_MAPPA));
    } catch {
      return SCELTA_SFONDO_PREDEFINITA;
    }
  }

  function impostaSfondoMappa(scelta: SceltaSfondo): void {
    sfondoMappa.value = normalizzaSceltaSfondo(scelta);
    try {
      $localStorage.setItem(CHIAVE_SFONDO_MAPPA, sfondoMappa.value);
    } catch {
      // archivio non disponibile: la scelta vale solo finché la pagina è aperta
    }
  }

  function leggiTemaSalvato(): boolean | null {
    if (!import.meta.client) return null;
    try {
      const nuovo = $localStorage.getItem(CHIAVE_TEMA);
      const vecchio = localStorage.getItem(CHIAVE_TEMA_VECCHIA);
      if (nuovo === null && vecchio !== null) {
        // migrazione: il valore della chiave senza suffisso passa a quella nuova
        $localStorage.setItem(CHIAVE_TEMA, vecchio);
      }
      if (vecchio !== null) localStorage.removeItem(CHIAVE_TEMA_VECCHIA);
      const valore = nuovo ?? vecchio;
      return valore === null ? null : valore === "true";
    } catch {
      return null;
    }
  }

  function scriviTemaSalvato(scuro: boolean): void {
    try {
      $localStorage.setItem(CHIAVE_TEMA, String(scuro));
    } catch {
      // archivio non disponibile (finestra privata, dati bloccati): il tema vale solo per la sessione
    }
  }

  // Da chiamare una volta all'avvio del client (plugin tema.client.ts): applica la scelta salvata.
  function caricaTemaSalvato(): void {
    const salvato = leggiTemaSalvato();
    temaScelto.value = salvato !== null;
    isDarkTheme.value = salvato === true;
    applicaClasseTema(isDarkTheme.value);
  }

  function impostaTema(scuro: boolean): void {
    isDarkTheme.value = scuro;
    temaScelto.value = true;
    applicaTemaSenzaTransizioni(scuro);
    scriviTemaSalvato(scuro);
  }

  // Rifiuto del suggerimento: il tema resta chiaro e la scelta si considera fatta.
  function rifiutaSuggerimentoTema(): void {
    impostaTema(false);
  }

  const apriMenu = () => (menuAperto.value = true);
  const chiudiMenu = () => (menuAperto.value = false);
  const alternaMenu = () => (menuAperto.value = !menuAperto.value);

  return { isDarkTheme, temaScelto, menuAperto, sfondoMappa, ultimoImpiantoStorico, impostaUltimoImpiantoStorico, caricaTemaSalvato, impostaTema, rifiutaSuggerimentoTema, impostaSfondoMappa, apriMenu, chiudiMenu, alternaMenu };
});
